import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Button } from '../design/primitives.tsx';
import styles from '../shell/Shell.module.css';

// Registers the service worker in production builds and offers the new
// version when one is waiting, instead of swapping code under the person.
export function UpdatePrompt() {
  const { needRefresh: [needRefresh, setNeedRefresh], updateServiceWorker } = useRegisterSW();
  if (!needRefresh) return null;
  return (
    <div className={styles.updateBar} role="status">
      <span>A new version of Guildite is ready.</span>
      <Button onClick={() => void updateServiceWorker(true)}>Update</Button>
      <Button variant="secondary" onClick={() => setNeedRefresh(false)}>Later</Button>
    </div>
  );
}

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferred = event as InstallEvent;
    listeners.forEach(listener => listener());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach(listener => listener());
  });
}

// The browser's install prompt, when it offers one.
export function useInstallPrompt(): (() => Promise<void>) | null {
  const [available, setAvailable] = useState(() => Boolean(deferred));
  useEffect(() => {
    const update = () => setAvailable(Boolean(deferred));
    listeners.add(update);
    return () => { listeners.delete(update); };
  }, []);
  if (!available) return null;
  return async () => {
    const event = deferred;
    if (!event) return;
    await event.prompt();
    await event.userChoice.catch(() => undefined);
    deferred = null;
    listeners.forEach(listener => listener());
  };
}
